import React from 'react'
import { Alert, Button, Col, Modal, Row, Space, Spin, Tag, Tooltip } from 'antd'
import styled from 'styled-components'
import { ProtocolInfographic, PILLARS } from '../components/ProtocolInfographic'
import ExecutiveSummary from '../components/ExecutiveSummary'

export const COMPARISON_MODEL_LABELS = {
  anthropic: 'Anthropic',
  deepseek: 'DeepSeek'
}

export const MODEL_ORDER = ['anthropic', 'deepseek']

const PILLAR_PROTOCOL_FIELDS = [
  ['exercise_protocol', 'exercise', 'Exercise Recommendations'],
  ['sleep_protocol', 'sleep', 'Sleep Recommendations'],
  ['nutrition_protocol', 'nutrition', 'Nutrition Recommendations']
]

const Panel = styled.div`
  border: ${(props) => (props.selected ? '2px solid #1677ff' : '1px solid #e8e8e8')};
  border-radius: 8px;
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 12px;
  margin-bottom: 12px;
`

const ModelName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
`

const MetaLine = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
`

const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: calc(78vh - 240px);
  padding-right: 6px;
`

const FieldLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const Muted = styled.div`
  font-size: 13px;
  color: #bfbfbf;
  font-style: italic;
`

const Spacer = styled.div`
  height: 16px;
`

const HtmlBlock = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.6;
  color: #5c626a;
  b {
    color: #262626;
  }
`

const PanelFooter = styled.div`
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
  margin-top: 12px;
`

const isEmpty = (value) => {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

const isSame = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch (err) {
    return false
  }
}

const formatCost = (cost) => {
  if (cost === null || cost === undefined) return null
  const num = Number(cost)
  if (Number.isNaN(num)) return null
  return `$${num.toFixed(4)}`
}

const formatGeneratedAt = (value) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString()
}

const DiffTag = ({ differs }) =>
  differs ? (
    <Tooltip title="This section differs between the two models">
      <Tag color="orange" style={{ marginLeft: 0 }}>
        differs
      </Tag>
    </Tooltip>
  ) : null

const toMarkdownHtml = (text) => {
  if (!text) return ''
  return String(text)
    .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
    .replace(/\*(.*?)\*/g, '<b>$1</b>')
}

const formatProtocolData = (protocol, type) => {
  const items = protocol && protocol[type]
  if (!items || !Array.isArray(items) || items.length === 0) return ''

  const phases = ['Phase 1:', 'Phase 2:', 'Phase 3:']
  let text = ''
  const appendRec = (rec) => {
    text += `**${rec.title}**\n\n`
    if (rec.why_this_matters) text += `${rec.why_this_matters}\n\n`
    if (rec.action_choices && rec.action_choices.length > 0) {
      text += 'Action Steps:\n'
      rec.action_choices.forEach((action) => {
        text += `- ${action}\n`
      })
      text += '\n'
    }
  }
  for (let i = 0; i < phases.length; i++) {
    const phaseRecs = items.slice(i * 2, i * 2 + 2)
    if (phaseRecs.length === 0) continue
    text += `\n${phases[i]}\n\n`
    phaseRecs.forEach(appendRec)
  }
  return text.trim()
}

const TextSection = ({ label, value, other }) => (
  <div>
    <FieldLabel>
      {label}
      <DiffTag differs={!isSame(value, other)} />
    </FieldLabel>
    {isEmpty(value) ? (
      <Muted>Not generated by this model</Muted>
    ) : (
      <HtmlBlock dangerouslySetInnerHTML={{ __html: toMarkdownHtml(value) }} />
    )}
  </div>
)

const ProtocolSection = ({ label, pillarKey, protocol, other }) => {
  const pillar = Array.isArray(PILLARS)
    ? PILLARS.find((p) => p && p.key === pillarKey)
    : undefined
  const infographic = protocol && protocol.protocol_infographic
  const fallbackText = formatProtocolData(protocol, `${pillarKey}_recommendations`)
  const otherFallback = other
    ? formatProtocolData(other, `${pillarKey}_recommendations`)
    : ''
  const differs = !isSame(
    infographic ? JSON.stringify(infographic) : fallbackText,
    other && other.protocol_infographic
      ? JSON.stringify(other.protocol_infographic)
      : otherFallback
  )
  return (
    <div>
      <FieldLabel>
        {label}
        <DiffTag differs={differs} />
      </FieldLabel>
      {isEmpty(protocol) ? (
        <Muted>Not generated by this model</Muted>
      ) : (
        <>
          {fallbackText ? (
            <HtmlBlock dangerouslySetInnerHTML={{ __html: toMarkdownHtml(fallbackText) }} />
          ) : (
            <Muted>No text recommendations generated by this model</Muted>
          )}
          {infographic && pillar && (
            <ProtocolInfographic
              data={infographic}
              pillar={pillar}
              editabled={false}
            />
          )}
        </>
      )}
    </div>
  )
}

const ModelPanel = ({ variant, data, meta, otherData, choosing, disabled, onChoose }) => {
  const label = COMPARISON_MODEL_LABELS[variant] || variant
  const cost = formatCost(meta && meta.cost)
  const generatedAt = formatGeneratedAt(meta && meta.generated_at)
  const failed = !data
  const isChoosing = choosing === variant

  return (
    <Panel selected={isChoosing}>
      <PanelHeader>
        <div>
          <ModelName>{label}</ModelName>
          <MetaLine>
            {[
              meta && meta.model_name,
              cost,
              generatedAt ? `generated ${generatedAt}` : null
            ]
              .filter(Boolean)
              .join(' \u00b7 ') || 'No metadata available'}
          </MetaLine>
        </div>
        {failed ? <Tag color="red">failed</Tag> : <Tag color="green">ready</Tag>}
      </PanelHeader>

      <PanelBody>
        {failed ? (
          <Alert
            type="error"
            showIcon
            message={`${label} did not return a response`}
            description={
              (meta && meta.error) ||
              'This model run failed. The other model is still available.'
            }
          />
        ) : (
          <>
            <TextSection
              label="Holistic Recommendations"
              value={data.intro}
              other={otherData ? otherData.intro : undefined}
            />
            <Spacer />
            {PILLAR_PROTOCOL_FIELDS.map(([key, pillarKey, fieldLabel]) => (
              <React.Fragment key={key}>
                <ProtocolSection
                  label={fieldLabel}
                  pillarKey={pillarKey}
                  protocol={data[key]}
                  other={otherData ? otherData[key] : undefined}
                />
                <Spacer />
              </React.Fragment>
            ))}
            <TextSection
              label="What to Expect"
              value={data.expectations}
              other={otherData ? otherData.expectations : undefined}
            />
            <Spacer />
            <TextSection
              label="Next Steps"
              value={data.support}
              other={otherData ? otherData.support : undefined}
            />
            {!isEmpty(data.executive_summary_data) && (
              <>
                <FieldLabel>
                  Executive Key Insights
                  <DiffTag
                    differs={ !isSame(
                      data.executive_summary_data,
                      otherData ? otherData.executive_summary_data : undefined
                    )}
                  />
                </FieldLabel>
                <ExecutiveSummary
                  data={data.executive_summary_data}
                  editabled={false}
                />
              </>
            )}
          </>
        )}
      </PanelBody>

      <PanelFooter>
        <Tooltip
          title={
            failed
              ? 'This model returned no response, so it cannot be used'
              : 'Use this deck as the AI recommendations for this assessment'
          }
        >
          <span style={{ display: 'block' }}>
            <Button
              type="primary"
              block
              disabled={failed || disabled}
              loading={isChoosing}
              onClick={() => onChoose(variant)}
            >
              Use this version
            </Button>
          </span>
        </Tooltip>
      </PanelFooter>
    </Panel>
  )
}

export default function ModelComparison({
  open,
  comparison,
  loading,
  choosing,
  regenerating,
  onClose,
  onChoose,
  onRegenerate
}) {
  if (!open) return null

  const source = comparison && comparison.source
  const anthropicBody = comparison && comparison.anthropic
  const deepseekBody = comparison && comparison.deepseek

  return (
    <Modal
      open={open}
      width="96vw"
      style={{ top: 20 }}
      footer={null}
      closable={!choosing}
      maskClosable={false}
      onCancel={onClose}
      bodyStyle={{ maxHeight: '82vh', overflowY: 'auto' }}
      title={
        !loading && comparison ? (
          <Space size={12} wrap>
            <span>Compare AI model outputs</span>
            {source === 'saved' && (
              <Tag color="blue">saved results &mdash; choose your preferred version</Tag>
            )}
            {source === 'generated' && <Tag color="purple">freshly generated</Tag>}
          </Space>
        ) : (
          regenerating ? 'Regenerating AI recommendations...' : 'Generating AI recommendations...'
        )
      }
    >
      {loading && !comparison ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin
            size="large"
            tip={
              regenerating
                ? 'Re-running both models... this can take a few minutes'
                : comparison
                  ? 'Loading model comparison...'
                  : 'Generating AI recommendations with comparative LLM models for a better version... this will take a few minutes'
            }
          >
            <div style={{ minHeight: 60 }} />
          </Spin>
        </div>
      ) : !comparison ? (
        <Alert
          type="warning"
          showIcon
          message="No comparison available"
          description="The model comparison could not be loaded. Please try regenerating, or close this window and edit the standard AI recommendations."
        />
      ) : comparison.needs_regenerate ? (
        <Alert
          type="warning"
          showIcon
          message="No model comparison saved for this assessment yet"
          description="This assessment already has AI recommendations, so a comparison is not generated automatically (that would re-run both models and cost time and money). Click below to deliberately generate both versions, or close this window to keep reading the existing deck."
          action={
            <Space direction="vertical">
              <Button type="primary" loading={!!regenerating} onClick={onRegenerate}>
                {regenerating ? 'Generating both versions...' : 'Generate comparison'}
              </Button>
              <Button onClick={onClose} disabled={!!regenerating}>
                Keep existing deck
              </Button>
            </Space>
          }
        />
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Read both decks and choose the better one"
            description="Both models generated a full assessment review from the same client data. The version you pick becomes the AI recommendations for this assessment and can then be edited as usual - the other version is discarded."
          />

          <Row gutter={16}>
            {MODEL_ORDER.map((variant) => (
              <Col span={12} key={variant}>
                <ModelPanel
                  variant={variant}
                  data={variant === 'anthropic' ? anthropicBody : deepseekBody}
                  otherData={variant === 'anthropic' ? deepseekBody : anthropicBody}
                  meta={comparison[`${variant}_meta`]}
                  choosing={choosing}
                  disabled={!!choosing || !!regenerating}
                  onChoose={onChoose}
                />
              </Col>
            ))}
          </Row>
        </>
      )}
    </Modal>
  )
}
